/**
 * Page URL Results List Component
 * Displays query results with delete buttons
 */

import React from 'react';
import { Card, Button, Label, Icon, Message } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { PageUrlElementModel, PageUrlType } from '../types';
import { i18nKeys, URL_TYPE_DISPLAY_NAMES } from '../constants';
import { StatusBadge } from '../../../../components/common/StatusBadge';
import {
  normaliseUrlType,
  getRecordEntity,
  getEntityTypeI18nKey,
  statusToVariant,
  hasStatus
} from '../helpers';
import styles from '../UrlUnlocking.module.css';

interface PageUrlResultsListProps {
  results: PageUrlElementModel[];
  isLoading: boolean;
  onDelete: (item: PageUrlElementModel) => void;
}

/**
 * Get display name for a normalised URL type (maps Collection to Superteam).
 * Falls back to the raw value if the type can't be normalised.
 */
const getUrlTypeDisplayName = (
  normalised: PageUrlType | undefined,
  raw: PageUrlElementModel['urlType']
): string => {
  if (normalised !== undefined && URL_TYPE_DISPLAY_NAMES[normalised]) {
    return URL_TYPE_DISPLAY_NAMES[normalised];
  }
  return `Type ${raw}`;
};

/**
 * Get color for URL type label
 */
const getUrlTypeColor = (urlType: PageUrlType | undefined): string => {
  switch (urlType) {
    case PageUrlType.Organization: return 'blue';
    case PageUrlType.Campaign: return 'green';
    case PageUrlType.Event: return 'purple';
    case PageUrlType.Fundraiser: return 'orange';
    case PageUrlType.Team: return 'teal';
    case PageUrlType.Collection: return 'pink';
    case PageUrlType.Series: return 'violet';
    default: return 'grey';
  }
};

/**
 * Render linked entity info
 */
const LinkedEntity: React.FC<{
  label: string;
  entity?: { id: number; name?: string; urlPath?: string };
}> = ({ label, entity }) => {
  if (!entity) return null;
  
  return (
    <div className={styles.linkedEntity}>
      <span className={styles.linkedEntityLabel}>{label}:</span>
      <span className={styles.linkedEntityValue}>
        {entity.name || `ID: ${entity.id}`}
        {entity.urlPath && <span className={styles.linkedEntityPath}> ({entity.urlPath})</span>}
      </span>
    </div>
  );
};

const PageUrlResultsList: React.FC<PageUrlResultsListProps> = ({
  results,
  isLoading,
  onDelete
}) => {
  if (isLoading) {
    return null; // Loading state handled by parent
  }

  if (results.length === 0) {
    return (
      <Message info>
        <Message.Header><Translate value={i18nKeys.RESULTS.NO_DATA} /></Message.Header>
      </Message>
    );
  }

  return (
    <div className={styles.resultsList}>
      <h3><Translate value={i18nKeys.RESULTS.TITLE} /> ({results.length})</h3>
      <Card.Group>
        {results.map((item) => {
          const normalisedType = normaliseUrlType(item.urlType);
          const showFundraiser = normalisedType === PageUrlType.Fundraiser;
          const showTeam = normalisedType === PageUrlType.Team;
          const showCollection = normalisedType === PageUrlType.Collection;
          const recordEntity = getRecordEntity(item);

          return (
            <Card key={item.id} fluid className={styles.resultCard}>
              <Card.Content>
                <Card.Header className={styles.resultHeader}>
                  <div className={styles.resultHeaderLeft}>
                    <Label color={getUrlTypeColor(normalisedType) as any} size="small">
                      {getUrlTypeDisplayName(normalisedType, item.urlType)}
                    </Label>
                    <span className={styles.resultId}>
                      <Translate value={i18nKeys.RESULTS.ID} />: {item.id}
                    </span>
                    {recordEntity && (
                      <span className={styles.resultId}>
                        {I18n.t(getEntityTypeI18nKey(item.urlType) ?? i18nKeys.RESULTS.ID)}{' '}
                        <Translate value={i18nKeys.RESULTS.ID} />: {recordEntity.id}
                      </span>
                    )}
                    {recordEntity && hasStatus(recordEntity) && (
                      <StatusBadge
                        label={recordEntity.status}
                        colorVariant={statusToVariant(recordEntity.status)}
                      />
                    )}
                  </div>
                  <Button
                    negative
                    size="small"
                    onClick={() => onDelete(item)}
                    className={styles.deleteButton}
                  >
                    <Icon name="trash" />
                    <Translate value={i18nKeys.RESULTS.DELETE_BUTTON} />
                  </Button>
                </Card.Header>

                <Card.Description className={styles.resultDetails}>
                  <div className={styles.resultRow}>
                    <strong><Translate value={i18nKeys.RESULTS.URL_PATH} />:</strong>
                    <code>{item.urlPath}</code>
                  </div>

                  <div className={styles.linkedEntities}>
                    <strong><Translate value={i18nKeys.RESULTS.LINKED_ENTITIES} />:</strong>
                    
                    {/* Always show Org, Event, Campaign if populated */}
                    <LinkedEntity
                      label={I18n.t(i18nKeys.RESULTS.ORGANIZATION)}
                      entity={item.organization}
                    />
                    <LinkedEntity
                      label={I18n.t(i18nKeys.RESULTS.EVENT)}
                      entity={item.event}
                    />
                    <LinkedEntity
                      label={I18n.t(i18nKeys.RESULTS.CAMPAIGN)}
                      entity={item.campaign}
                    />
                    
                    {/* Show Series if populated */}
                    <LinkedEntity
                      label={I18n.t(i18nKeys.RESULTS.SERIES)}
                      entity={item.series}
                    />

                    {/* Show Fundraiser if type = Fundraiser */}
                    {showFundraiser && (
                      <LinkedEntity
                        label={I18n.t(i18nKeys.RESULTS.FUNDRAISER)}
                        entity={item.fundraiser}
                      />
                    )}

                    {/* Show Team if type = Team */}
                    {showTeam && (
                      <LinkedEntity
                        label={I18n.t(i18nKeys.RESULTS.TEAM)}
                        entity={item.team}
                      />
                    )}

                    {/* Show Collection/Superteam if type = Collection */}
                    {showCollection && (
                      <LinkedEntity
                        label={I18n.t(i18nKeys.RESULTS.SUPERTEAM)}
                        entity={item.collection}
                      />
                    )}

                    {/* Show message if no linked entities */}
                    {!item.organization && !item.event && !item.campaign && 
                     !item.series && !item.fundraiser && !item.team && !item.collection && (
                      <div className={styles.noLinkedEntities}>
                        <em>No linked entities found</em>
                      </div>
                    )}
                  </div>
                </Card.Description>
              </Card.Content>
            </Card>
          );
        })}
      </Card.Group>
    </div>
  );
};

export default PageUrlResultsList;
